import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { RED, RED_LIGHT, MUTED } from "assets/theme/brandColors";

// A technique stops explaining itself once the cook is Comfortable with it.
// Kept here as data rather than a chain of comparisons so the "collapse at
// Comfortable" rule reads as one thing.
const COLLAPSED_TIERS = new Set(["Comfortable", "Instinct"]);

/**
 * Whether a technique's *rationale sentence* should fold out of the step prose
 * for this user.
 *
 * Two ways in, and the manual one comes first on purpose: somebody who has
 * heard why salt goes on meat twenty times should not have to wait for a rep
 * counter to agree with them before the app stops saying it.
 *
 * The chips below no longer consult this — they start collapsed for everyone.
 * This governs only the prose fold in RecordingCard, which is why a "Got it"
 * dismissal still changes something the cook can see.
 */
export function isCollapsed(state, techniqueId) {
  const entry = state?.[techniqueId];
  if (!entry) return false;
  return Boolean(entry.dismissed) || COLLAPSED_TIERS.has(entry.tier);
}

/**
 * Technique chips for one cooking step.
 *
 * The recipe prose is never touched — this is an additive layer beside it, so
 * the audio stays authoritative and a step reads exactly as it was recorded.
 *
 * What arrives here has already been filtered and capped server-side:
 * deferred techniques are absent, `why_it_works` is present only on the first
 * encounter of a technique within the recipe, and it is withheld entirely on
 * steps whose own prose already carries the reasoning. A chip with no
 * `why_it_works` is a label, not an explanation, and renders as one.
 *
 * **Every chip starts collapsed to its one-line blurb, and a tap opens it.**
 * The explanation used to be expanded by default for anyone below Comfortable
 * on the technique — which is every new cook, on every step. In the cooking
 * view that put a paragraph between the step instructions and the "Mark step
 * done" button, and it is not addressed to the moment: a rice recipe served
 * 55 words on collagen in tough cuts of meat while the cook was standing over
 * a pan. Collapsing is a display default, never a judgement about what the
 * cook knows — nothing is removed and one tap brings it back.
 *
 * Tier and "Got it" still do their work; they just do it on the rationale
 * sentence inside the prose, through `isCollapsed()` above.
 */
function TechniqueChips({ techniques, onDismiss, expandedIds, onToggleExpand }) {
  const shown = (techniques || []).filter((t) => t.salience === "primary");
  if (shown.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
      {shown.map((technique) => {
        const expanded = Boolean(expandedIds?.has(technique.id));
        const hasExplanation = Boolean(technique.why_it_works);

        return (
          <Box
            key={technique.id}
            sx={{
              border: "1.5px solid",
              borderColor: expanded && hasExplanation ? RED : "#ddd8d0",
              borderLeft: expanded && hasExplanation ? "4px solid" : "1.5px solid",
              borderLeftColor: expanded && hasExplanation ? RED : "#ddd8d0",
              bgcolor: "#fff",
              borderRadius: "10px",
              px: 1.5,
              py: 1,
            }}
          >
            <Box
              component="button"
              onClick={() => hasExplanation && onToggleExpand(technique.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                border: "none",
                borderRadius: 0,
                bgcolor: "#fff",
                p: 0,
                textAlign: "left",
                cursor: hasExplanation ? "pointer" : "default",
                appearance: "none",
                font: "inherit",
              }}
            >
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.4px",
                  textTransform: "uppercase",
                  color: RED,
                  bgcolor: RED_LIGHT,
                  borderRadius: "999px",
                  px: "8px",
                  py: "2px",
                  whiteSpace: "nowrap",
                }}
              >
                {technique.display_name}
              </Box>
              {technique.blurb && (
                <Typography
                  variant="caption"
                  sx={{ color: MUTED, fontSize: 12, lineHeight: 1.5, flex: 1 }}
                >
                  {technique.blurb}
                </Typography>
              )}
              {/* The affordance. A collapsed chip that looks like a plain
                  label is a paragraph nobody finds. */}
              {hasExplanation && (
                <Typography
                  variant="caption"
                  sx={{ color: RED, fontSize: 11, fontWeight: 700, flexShrink: 0 }}
                >
                  {expanded ? "Hide" : "Why?"}
                </Typography>
              )}
            </Box>

            {expanded && hasExplanation && (
              <>
                <Box
                  sx={{
                    bgcolor: RED_LIGHT,
                    borderRadius: "8px",
                    p: "10px 12px",
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 13, lineHeight: 1.65, color: "#3a3a3a" }}
                  >
                    {technique.why_it_works}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                  <Button
                    size="small"
                    onClick={() => onDismiss(technique.id)}
                    sx={{
                      fontSize: 11.5,
                      textTransform: "none",
                      fontWeight: 600,
                      color: MUTED,
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    Got it
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

TechniqueChips.propTypes = {
  techniques: PropTypes.array,
  onDismiss: PropTypes.func.isRequired,
  expandedIds: PropTypes.instanceOf(Set),
  onToggleExpand: PropTypes.func.isRequired,
};

export default TechniqueChips;
